/**
 * ModalFooter Component
 *
 * Standardized footer for modals with consistent spacing, border, and button gap.
 * Ensures all modals have the same visual rhythm — matching the split button gap pattern.
 *
 * Usage:
 *   <ModalFooter>
 *     <CancelButton onClick={onClose} />
 *     <Button variant="primary">Save</Button>
 *   </ModalFooter>
 */

interface ModalFooterProps {
  children: React.ReactNode;
  /** Additional classes for the outer wrapper */
  className?: string;
  /** Use negative margins to break out of parent padding (e.g. inside scrollable content) */
  flush?: boolean;
  /** Justify content — defaults to 'end' */
  justify?: 'end' | 'center' | 'between' | 'center-lg-end';
  /** Hide the top border */
  noBorder?: boolean;
}

const justifyMap = {
  'end': 'justify-end',
  'center': 'justify-center',
  'between': 'justify-between',
  'center-lg-end': 'justify-center lg:justify-end',
};

export const ModalFooter = ({ children, className = '', flush = false, justify = 'end', noBorder = false }: ModalFooterProps) => (
  <div
    className={`${noBorder ? '' : 'border-t border-gray-200'} px-6 py-4 rounded-b-lg ${
      flush ? '-mx-6 -mb-4 mt-6 pt-4 pb-4' : ''
    } ${className}`}
  >
    <div className={`flex items-center ${justifyMap[justify]} gap-1.5 sm:gap-2`}>
      {children}
    </div>
  </div>
);
